import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import styled, { useTheme } from "styled-components/macro";
import { TertiaryButtonMedium } from "../Buttons/Buttons";
import { DropDownModal } from "../DropDownModal/DropDownModal";
import { CaretDownIcon } from "../Icons/Icons";

const DROPDOWN_OPEN_EVENT = "agilis:dropdown-open";

interface IDropDownButtonProps {
  hideBorder?: boolean | undefined;
}

interface IDropDownProps {
  className?: string | undefined;
  activeItem: string | number;
  clickHandler: (val: any) => void;
  direction: "right" | "left";
  textLeft?: string | undefined;
  textRight?: string | undefined;
  items: number[] | string[] | JSX.Element[];
  showIcon?: boolean | undefined;
  hideBorder?: boolean | undefined;
  placeholder?: string | JSX.Element | undefined;
}

const DropDownWrapper = styled.div<IDropDownButtonProps>`
  display: inline-block;
  position: relative;
  button {
    cursor: pointer;
    color: ${({ theme }) => theme.secondaryTextColor};
    font-size: ${({ theme }) => theme.fontSizes.small};
    border-width: ${({ theme, hideBorder }) =>
      hideBorder ? theme.resolve("border_none") : theme.resolve("border_md")};
    &:hover,
    &:focus,
    &:active {
      border-width: ${({ theme, hideBorder }) =>
        hideBorder ? theme.resolve("border_none") : theme.resolve("border_md")};
    }
  }
`;
const DropDownModalWrapper = styled.div`
  position: fixed;
  z-index: 900;
  width: 160px;
`;

const FlexTertiaryButtonMedium = styled(TertiaryButtonMedium)`
  display: flex;
  align-items: center;
  & > span {
    margin-right: 3px;
  }
  & > span:last-child {
    margin-right: 0;
  }
`;

export const DropDown = ({
  hideBorder = true,
  direction = "right",
  showIcon = true,
  ...props
}: IDropDownProps) => {
  const [activeItem, setActive] = useState(props.activeItem);
  const [showDropDown, setShowDropDown] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number }>(
    { top: 0, left: 0 }
  );
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const dropdownIdRef = useRef<string>(Math.random().toString(36).slice(2));

  const handleClick = (e: any) => {
    e.preventDefault();
    setActive(
      Number(e.currentTarget.dataset.name)
        ? Number(e.currentTarget.dataset.name)
        : e.currentTarget.dataset.name
    );
    setShowDropDown(false);
    props.clickHandler(
      Number(e.currentTarget.dataset.name)
        ? Number(e.currentTarget.dataset.name)
        : e.currentTarget.dataset.name
    );
  };

  useEffect(() => {
    setActive(Number(activeItem) ? Number(activeItem) : activeItem);
    setShowDropDown(false);
    props.clickHandler(Number(activeItem) ? Number(activeItem) : activeItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node;
    if (
      wrapperRef.current?.contains(target) ||
      dropdownRef.current?.contains(target)
    ) {
      return;
    }
    setShowDropDown(false);
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, false);
    return () => {
      document.removeEventListener("click", handleClickOutside, false);
    };
  }, [handleClickOutside]);

  const calculateDropdownPosition = useCallback(() => {
    const width = 160;
    const dropdownHeight = 150;
    const margin = 15;

    if (!wrapperRef.current) {
      return { top: 0, left: 0 };
    }
    const rect = wrapperRef.current.getBoundingClientRect();

    const leftBase = direction === "right" ? rect.right - width : rect.left;

    const left = Math.min(
      Math.max(leftBase, margin),
      window.innerWidth - width - margin
    );

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    let top;
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      top = rect.top - dropdownHeight;
    } else {
      top = rect.bottom;
    }

    return { top, left };
  }, [direction]);

  const openDropdown = () => {
    setDropdownPos(calculateDropdownPosition());
    setShowDropDown((prev) => {
      const nextState = !prev;
      if (nextState) {
        window.dispatchEvent(
          new CustomEvent(DROPDOWN_OPEN_EVENT, {
            detail: { id: dropdownIdRef.current },
          })
        );
      }
      return nextState;
    });
  };

  useEffect(() => {
    const handleAnotherDropdownOpened = (event: Event) => {
      const customEvent = event as CustomEvent<{ id?: string }>;
      if (customEvent.detail?.id !== dropdownIdRef.current) {
        setShowDropDown(false);
      }
    };

    window.addEventListener(DROPDOWN_OPEN_EVENT, handleAnotherDropdownOpened);

    return () => {
      window.removeEventListener(
        DROPDOWN_OPEN_EVENT,
        handleAnotherDropdownOpened
      );
    };
  }, []);

  useEffect(() => {
    if (!showDropDown) return;

    const updatePosition = () => {
      setDropdownPos(calculateDropdownPosition());
    };

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [showDropDown, calculateDropdownPosition]);

  return (
    <DropDownWrapper
      ref={wrapperRef}
      hideBorder={hideBorder}
      className={props.className}
      style={{
        marginRight: direction === "right" ? "0 !important" : "",
        marginLeft: direction === "right" ? "auto" : "0",
      }}
    >
      <FlexTertiaryButtonMedium
        onClick={(e) => {
          e.stopPropagation();
          openDropdown();
        }}
      >
        {!!!props.placeholder && (
          <>
            <span style={{ color: theme.secondaryTextColor }}>
              {props.textLeft}{" "}
            </span>
            <span style={{ color: theme.primaryTextColor }}>{activeItem}</span>
            <span
              style={{ color: theme.primaryTextColor, whiteSpace: "nowrap" }}
            >
              {props.textRight}
            </span>
          </>
        )}
        {!!props.placeholder && <span>{props.placeholder}</span>}
        {showIcon && (
          <CaretDownIcon
            fill={theme.secondaryTextColor}
            width={16}
            height={16}
          />
        )}
      </FlexTertiaryButtonMedium>
      {showDropDown &&
        typeof document !== "undefined" &&
        createPortal(
          <DropDownModalWrapper
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
            ref={dropdownRef}
          >
            <DropDownModal
              items={props.items}
              activeItem={props.activeItem}
              clickHandler={handleClick}
            />
          </DropDownModalWrapper>,
          document.body
        )}
    </DropDownWrapper>
  );
};
